import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

export interface OpcaoIdDescricao {
  id: number;
  descricao: string;
}

interface AjaxApiResponse {
  hasErrors?: boolean;
  HasErrors?: boolean;
  message?: string;
  Message?: string;
}

export interface ObservacaoContaReceberPayload {
  idContaReceber: number;
  observacao: string;
}

export interface ContaReceberListaItem {
  id: number;
  usuarioPagador: string;
  finDocumento: string;
  previsaoReal: string;
  idCentroCusto: number;
  centroCusto: string;
  parcela: number;
  dataEmissao: string;
  dataVencimento: string;
  dataRecebimento: string;
  valorDocumento: number | null;
  valorRecebido: number | null;
  pago: boolean;
  boletoGerado: boolean;
  cnabEnviado: boolean;
  boletoRegistradoApi: boolean;
  /** Preenchido após remessa CNAB (Santander). */
  nossoNumero: string | null;
  tipoRecebimento: string;
  observacao: string;
  dataEmissaoOrdenacao: number;
}

@Injectable({
  providedIn: 'root'
})
export class FinContaReceberListaService {
  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  /**
   * Carrega opções (centros de custo para descrição) e a lista de contas a receber.
   * `params` usa as mesmas chaves do endpoint ConsultarFinContaReceber (opcional).
   */
  carregarLista(params?: URLSearchParams): Observable<ContaReceberListaItem[]> {
    return this.carregarListaPorEndpoint('ConsultarFinContaReceber', params);
  }

  carregarListaGerarCnab(params?: URLSearchParams): Observable<ContaReceberListaItem[]> {
    return this.carregarListaPorEndpoint('ConsultarFinContaReceberGerarCnab', params);
  }

  private carregarListaPorEndpoint(endpoint: string, params?: URLSearchParams): Observable<ContaReceberListaItem[]> {
    const query = params?.toString();
    const url = `${API}financeiro/FinContaReceber/${endpoint}${query ? `?${query}` : ''}`;

    return forkJoin({
      centros: this.ajaxService.get(`${API}financeiro/centrocustos`),
      lista: this.ajaxService.get(url)
    }).pipe(
      map(({ centros, lista }: { centros: AjaxApiResponse; lista: AjaxApiResponse }) => {
        const centrosCustos = centros?.hasErrors
          ? []
          : this.util.mapearOpcoesCentroCusto(this.util.extrairListaResponse(centros));

        if (lista?.hasErrors) {
          return [];
        }
        const itensLista = this.extrairListaResponse(lista);
        return itensLista
          .map((x: any) => this.mapearItem(x, centrosCustos))
          .filter((x: ContaReceberListaItem) => x.id > 0)
          .sort((a: ContaReceberListaItem, b: ContaReceberListaItem) => {
            if (a.dataEmissaoOrdenacao !== b.dataEmissaoOrdenacao) {
              return b.dataEmissaoOrdenacao - a.dataEmissaoOrdenacao;
            }
            return b.id - a.id;
          });
      })
    );
  }

  private mapearItem(x: any, centrosCustos: OpcaoIdDescricao[]): ContaReceberListaItem {
    const item = this.util.converterParaLowercase(x || {});
    const idCentroCusto = Number(item.idcentrocusto || 0);
    return {
      id: Number(item.id || 0),
      usuarioPagador: String(item.usuariopagador || ''),
      finDocumento: String(item.findocumento || ''),
      previsaoReal: String(item.previsaoreal || 'R').toUpperCase() === 'P' ? 'P' : 'R',
      idCentroCusto,
      centroCusto: this.resolverDescricaoPorId(centrosCustos, idCentroCusto),
      parcela: Number(item.parcela || 0),
      dataEmissao: this.formatarData(item.dataemissao),
      dataVencimento: this.formatarData(item.datavencimento),
      dataRecebimento: this.formatarData(item.datarecebimento),
      valorDocumento: item.valordocumento != null ? Number(item.valordocumento) : null,
      valorRecebido: item.valorrecebido != null ? Number(item.valorrecebido) : null,
      pago: Boolean(item.pago),
      boletoGerado: Boolean(item.boletogerado),
      cnabEnviado: Boolean(item.cnabenviado),
      boletoRegistradoApi: Boolean(
        item.boletoregistradoapi ?? item.boletoRegistradoApi ?? item.cnabenviado
      ),
      nossoNumero: item.nossonumero != null && String(item.nossonumero).trim() !== '' ? String(item.nossonumero) : null,
      tipoRecebimento: String(item.tiporecebimento || ''),
      observacao: String(item.observacao || ''),
      dataEmissaoOrdenacao: this.obterTimestampData(item.dataemissao)
    };
  }

  private formatarData(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('pt-BR');
  }

  private obterTimestampData(value: any): number {
    if (!value) return 0;
    const texto = String(value).trim();
    const padraoBr = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (padraoBr) {
      const dia = Number(padraoBr[1]);
      const mes = Number(padraoBr[2]);
      const ano = Number(padraoBr[3]);
      return new Date(ano, mes - 1, dia).getTime();
    }
    const data = new Date(texto);
    const timestamp = data.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) {
        return [data];
      }
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }

  private resolverDescricaoPorId(lista: OpcaoIdDescricao[], id: number): string {
    if (!id || id <= 0) return '-';
    const encontrado = lista.find((x) => x.id === id);
    return encontrado?.descricao || '-';
  }
}
